import { config } from '../config/env';
import api from '../config/axios';

// Utilitários para dataLayer (GTM) usando nomes recomendados do GA4
// https://developers.google.com/analytics/devguides/collection/ga4/reference/events

const TRACKING_USER_DATA_KEY = '__gioTrackingUserData';
const ATTRIBUTION_CACHE_KEY = '__gioAttributionCache';
const ATTRIBUTION_CACHE_TTL = 5 * 60 * 1000; // 5 minutos de cache

const normalizeEmail = (email) => {
  if (!email) return undefined;
  const v = String(email).trim().toLowerCase();
  return v || undefined;
};

const normalizePhoneToDigits = (phone, defaultCountry = '55') => {
  if (!phone) return undefined;
  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return undefined;

  // Se já vier com DDI (ex: 55...), mantém.
  if (digits.length >= 12) return digits;

  // Para BR (padrão), prefixa 55 quando vier só DDD+numero (10/11 dígitos).
  if ((digits.length === 10 || digits.length === 11) && defaultCountry) {
    return `${defaultCountry}${digits}`;
  }

  return digits;
};

const normalizePostalCode = (postalCode) => {
  if (!postalCode) return undefined;
  const digits = String(postalCode).replace(/\D/g, '');
  return digits || undefined;
};

const normalizeState = (state) => {
  if (!state) return undefined;
  const v = String(state).trim().toUpperCase();
  return v || undefined;
};

const normalizeCity = (city) => {
  if (!city) return undefined;
  const v = String(city).trim();
  return v || undefined;
};

const normalizeCountry = (country) => {
  if (!country) return 'BR';
  const v = String(country).trim().toUpperCase();
  return v || 'BR';
};

const splitFullName = (fullName) => {
  if (!fullName) return { first_name: undefined, last_name: undefined };
  const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { first_name: undefined, last_name: undefined };
  if (parts.length === 1) return { first_name: parts[0], last_name: undefined };
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
};

const deepMerge = (base = {}, override = {}) => {
  const out = { ...(base || {}) };
  for (const [k, v] of Object.entries(override || {})) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = deepMerge(out[k], v);
    } else if (v !== undefined) {
      out[k] = v;
    }
  }
  return out;
};

// ===== FUNÇÕES PARA BUSCAR ATRIBUIÇÃO DO BANCO DE DADOS =====

/**
 * Obtém o user_id do usuário logado
 * Tenta buscar do localStorage ou do Supabase session
 */
const getCurrentUserId = () => {
  if (typeof window === 'undefined') return null;
  
  // Tentar obter do window (setado após login)
  if (window.__gioUserId) return window.__gioUserId;
  
  // Tentar obter do localStorage (Supabase auth)
  try {
    const supabaseAuth = localStorage.getItem('sb-auth-token');
    if (supabaseAuth) {
      const parsed = JSON.parse(supabaseAuth);
      if (parsed?.user?.id) {
        window.__gioUserId = parsed.user.id;
        return parsed.user.id;
      }
    }
    
    // Sessão do supabase-js v2: chave `sb-<host ou ref>-auth-token` (aqui `sb-app-auth-token`,
    // porque a URL é app.giobrain.com/supabase). Antes só se procurava 'supabase' no nome,
    // que essa chave não tem: o retorno do Stripe ficava sem user_id e não lia fbc/UTMs do banco.
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const isSupabaseSession = key && (
        (key.startsWith('sb-') && key.endsWith('-auth-token')) ||
        (key.includes('supabase') && key.includes('auth'))
      );
      if (isSupabaseSession) {
        const value = localStorage.getItem(key);
        if (value) {
          const parsed = JSON.parse(value);
          const userId = parsed?.user?.id || parsed?.currentSession?.user?.id;
          if (userId) {
            window.__gioUserId = userId;
            return userId;
          }
        }
      }
    }
  } catch (e) {
    console.warn('Erro ao obter user_id:', e);
  }
  
  return null;
};

/**
 * Define o user_id manualmente (chamar após login)
 */
export const setCurrentUserId = (userId) => {
  if (typeof window === 'undefined') return;
  window.__gioUserId = userId;
};

/**
 * Busca dados de atribuição do banco de dados
 * Retorna dados formatados para o GTM/dataLayer
 */
const fetchAttributionFromDB = async (userId) => {
  if (!userId) return null;
  
  try {
    const response = await api.get(`/api/user-utm-tracking/${userId}/attribution/`);
    
    if (response.data) {
      console.log('📊 Attribution - Dados carregados do banco:', response.data);
      return response.data;
    }
    return null;
  } catch (error) {
    console.warn('📊 Attribution - Erro ao buscar do banco (usando sessão):', error.message);
    return null;
  }
};

/**
 * Busca dados de atribuição com cache
 * Prioriza dados do banco, fallback para localStorage/sessionStorage
 * 
 * Ordem de prioridade:
 * 1. Cache em memória (se válido)
 * 2. Banco de dados (Supabase user_utm_tracking)
 * 3. localStorage (gio_utm_data do utmCapture)
 * 4. sessionStorage (tags do GTM)
 * 5. URL atual (parâmetros UTM)
 */
export const getAttributionData = async () => {
  if (typeof window === 'undefined') return {};
  
  const userId = getCurrentUserId();
  
  // 1. Verificar cache em memória
  const cached = window[ATTRIBUTION_CACHE_KEY];
  if (cached && cached.userId === userId && (Date.now() - cached.timestamp) < ATTRIBUTION_CACHE_TTL) {
    console.log('📊 Attribution - Usando cache em memória');
    return cached.data;
  }
  
  // 2. Se tem user_id, tentar buscar do banco
  if (userId) {
    try {
      const dbData = await fetchAttributionFromDB(userId);
      
      // _fbc/_fbp também valem: é o que liga o evento ao clique no anúncio (Meta CAPI).
      if (dbData && (dbData.first_touch_source || dbData.last_touch_source || dbData.gclid || dbData.fbclid
        || dbData.ttclid || dbData.ttp || dbData._fbc || dbData._fbp)) {
        // Cache dos dados do banco
        window[ATTRIBUTION_CACHE_KEY] = {
          userId,
          timestamp: Date.now(),
          data: dbData,
          source: 'database',
        };
        console.log('📊 Attribution - Dados obtidos do Supabase:', {
          source: dbData.first_touch_source,
          campaign: dbData.first_touch_campaign,
          gclid: dbData.gclid ? 'presente' : 'ausente',
          fbclid: dbData.fbclid ? 'presente' : 'ausente',
        });
        return dbData;
      }
    } catch (error) {
      console.warn('📊 Attribution - Erro ao buscar do banco, usando fallback local:', error.message);
      // Continua para o fallback
    }
  }
  
  // 3. Fallback: buscar do localStorage/sessionStorage/URL
  const sessionData = getSessionUTMs();
  
  // Se encontrou dados, fazer cache também (com TTL menor)
  if (sessionData && Object.keys(sessionData).length > 0) {
    window[ATTRIBUTION_CACHE_KEY] = {
      userId,
      timestamp: Date.now(),
      data: sessionData,
      source: 'local_fallback',
    };
  }
  
  return sessionData;
};

/**
 * Busca UTMs do localStorage/sessionStorage (fallback robusto)
 * Prioridade: localStorage (utmCapture) > sessionStorage (GTM)
 */
const getSessionUTMs = () => {
  try {
    const utmData = {};
    
    // 1. Tentar buscar do localStorage (salvo pelo utmCapture.js)
    const savedUTM = localStorage.getItem('gio_utm_data');
    if (savedUTM) {
      try {
        const parsed = JSON.parse(savedUTM);
        if (parsed) {
          if (parsed.utm_source) {
            utmData.first_touch_source = parsed.utm_source;
            utmData.last_touch_source = parsed.utm_source;
          }
          if (parsed.utm_medium) {
            utmData.first_touch_medium = parsed.utm_medium;
            utmData.last_touch_medium = parsed.utm_medium;
          }
          if (parsed.utm_campaign) {
            utmData.first_touch_campaign = parsed.utm_campaign;
            utmData.last_touch_campaign = parsed.utm_campaign;
          }
          if (parsed.utm_content) {
            utmData.first_touch_content = parsed.utm_content;
            utmData.last_touch_content = parsed.utm_content;
          }
          if (parsed.utm_term) {
            utmData.first_touch_term = parsed.utm_term;
            utmData.last_touch_term = parsed.utm_term;
          }
          if (parsed.utm_id) utmData.utm_id = parsed.utm_id;
          if (parsed.utm_source_platform) utmData.utm_source_platform = parsed.utm_source_platform;
          if (parsed.gclid) utmData.gclid = parsed.gclid;
          if (parsed.fbclid) utmData.fbclid = parsed.fbclid;
          if (parsed.ttclid) utmData.ttclid = parsed.ttclid;
          if (parsed.msclkid) utmData.msclkid = parsed.msclkid;
        }
      } catch (parseError) {
        console.warn('📊 Attribution - Erro ao parsear gio_utm_data:', parseError);
      }
    }

    // 1b. Tracking IDs de cookies (gio_tracking_ids salvo pelo utmCapture.js)
    // Contém _fbp, _fbc, _ttp, ttp, ttclid, etc.
    const savedTrackingIds = localStorage.getItem('gio_tracking_ids');
    if (savedTrackingIds) {
      try {
        const ids = JSON.parse(savedTrackingIds);
        if (ids._fbc) utmData._fbc = ids._fbc;
        if (ids._fbp) utmData._fbp = ids._fbp;
        if (ids._ttp || ids.ttp) utmData.ttp = ids._ttp || ids.ttp;
        if (ids._ttclid || ids.ttclid) utmData.ttclid = utmData.ttclid || ids._ttclid || ids.ttclid;
        if (ids._uetmsclkid) utmData.msclkid = utmData.msclkid || ids._uetmsclkid;
      } catch (_) {}
    }

    if (Object.keys(utmData).length > 0) {
      console.log('📊 Attribution - Fallback: localStorage (utmCapture)', utmData);
      return utmData;
    }
    
    // 2. Fallback: buscar do sessionStorage (salvo pelas tags do GTM)
    const keys = [
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
      'utm_id', 'utm_source_platform',
      'gclid', 'fbclid', 'ttclid', 'msclkid',
    ];
    const firstTouchUtmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    
    keys.forEach(key => {
      const value = sessionStorage.getItem(key);
      if (value) {
        if (firstTouchUtmKeys.includes(key)) {
          const shortKey = key.replace('utm_', '');
          utmData[`first_touch_${shortKey}`] = utmData[`first_touch_${shortKey}`] || value;
          utmData[`last_touch_${shortKey}`] = utmData[`last_touch_${shortKey}`] || value;
        } else {
          utmData[key] = utmData[key] || value;
        }
      }
    });
    
    // 3. Último fallback: tentar buscar da URL atual
    if (Object.keys(utmData).length === 0) {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        keys.forEach(key => {
          const value = urlParams.get(key);
          if (value) {
            if (firstTouchUtmKeys.includes(key)) {
              const shortKey = key.replace('utm_', '');
              utmData[`first_touch_${shortKey}`] = value;
              utmData[`last_touch_${shortKey}`] = value;
            } else {
              utmData[key] = value;
            }
          }
        });
      } catch (urlError) {
        // Ignorar erros de URL
      }
    }
    
    if (Object.keys(utmData).length > 0) {
      console.log('📊 Attribution - Fallback: sessionStorage/URL', utmData);
    }
    
    return utmData;
  } catch (e) {
    console.warn('📊 Attribution - Erro no fallback:', e);
    return {};
  }
};

/**
 * Limpa o cache de atribuição (chamar após logout)
 */
export const clearAttributionCache = () => {
  if (typeof window === 'undefined') return;
  delete window[ATTRIBUTION_CACHE_KEY];
  delete window.__gioUserId;
};

/**
 * Valida se _fbc está no formato Meta: fb.{subdomainIndex}.{creationTimeMs}.{fbclid}
 */
const isValidFbc = (value) => {
  if (!value || typeof value !== 'string') return false;
  return /^fb\.\d+\.\d+\.[A-Za-z0-9_-]+$/.test(value);
};

/**
 * Monta _fbc no formato Meta a partir do fbclid bruto.
 * Usado quando o Pixel não criou o cookie _fbc.
 */
const buildFbcFromFbclid = (fbclid) => {
  if (!fbclid || typeof fbclid !== 'string') return null;
  const cleaned = fbclid.trim();
  if (!cleaned) return null;
  return `fb.1.${Date.now()}.${cleaned}`;
};

/**
 * Resolve _fbc: banco → cookie → sintetizado a partir de fbclid
 */
const resolveFbc = (dbFbc, cookieFbc, fbclid) => {
  if (isValidFbc(dbFbc)) return { fbc: dbFbc, source: 'banco' };
  if (isValidFbc(cookieFbc)) return { fbc: cookieFbc, source: 'cookie' };
  // Aceitar valor existente mesmo se formato levemente diferente (não sobrescrever)
  if (dbFbc) return { fbc: dbFbc, source: 'banco' };
  if (cookieFbc) return { fbc: cookieFbc, source: 'cookie' };
  const synthesized = buildFbcFromFbclid(fbclid);
  if (synthesized) return { fbc: synthesized, source: 'fbclid' };
  return { fbc: null, source: null };
};

/**
 * Enriquece parâmetros do evento com dados de atribuição do banco
 * Retorna os parâmetros enriquecidos + metadata sobre a fonte
 */
const enrichWithAttributionFromDB = async (parameters = {}) => {
  const attribution = await getAttributionData();
  
  // Verificar fonte dos dados (do cache)
  const cached = window[ATTRIBUTION_CACHE_KEY];
  const dataSource = cached?.source || 'unknown';
  
  if (!attribution || Object.keys(attribution).length === 0) {
    console.log('📊 Attribution - Nenhum dado de atribuição encontrado (sem UTMs/click IDs)');
    return {
      ...parameters,
      _attribution_source: 'none',
    };
  }
  
  const hasDbData = dataSource === 'database';
  const hasClickIds = attribution.gclid || attribution.fbclid || attribution.ttclid || attribution.ttp || attribution.msclkid;
  const hasUtms = attribution.first_touch_source || attribution.first_touch_campaign;
  
  console.log(`📊 Attribution - Fonte: ${hasDbData ? '✅ Supabase' : '⚠️ Fallback local'}`, {
    hasUtms,
    hasClickIds,
    source: attribution.first_touch_source,
    campaign: attribution.first_touch_campaign,
  });
  
  // Obter _fbc/_fbp do banco (prioridade) ou do cookie do browser (fallback)
  const dbFbc = attribution._fbc || null;
  const dbFbp = attribution._fbp || null;
  const cookieFbc = typeof document !== 'undefined'
    ? (() => {
        try {
          const raw = document.cookie.match(/(?:^|;\s*)_fbc=([^;]*)/)?.[1];
          return raw ? decodeURIComponent(raw) : null;
        } catch {
          return null;
        }
      })()
    : null;
  const cookieFbp = typeof document !== 'undefined'
    ? (() => {
        try {
          const raw = document.cookie.match(/(?:^|;\s*)_fbp=([^;]*)/)?.[1];
          return raw ? decodeURIComponent(raw) : null;
        } catch {
          return null;
        }
      })()
    : null;

  const fbclid = attribution.fbclid || null;
  const { fbc, source: fbcSource } = resolveFbc(dbFbc, cookieFbc, fbclid);
  const fbp = dbFbp || cookieFbp;

  if (fbc || fbp) {
    console.log('📊 Attribution - Facebook cookies:', {
      fbc: fbc ? `✅ (${fbcSource})` : '❌',
      fbp: fbp ? `✅ (${dbFbp ? 'banco' : 'cookie'})` : '❌',
    });
  }

  return {
    ...parameters,
    // First-touch attribution
    first_touch_source: attribution.first_touch_source || null,
    first_touch_medium: attribution.first_touch_medium || null,
    first_touch_campaign: attribution.first_touch_campaign || null,
    first_touch_content: attribution.first_touch_content || null,
    first_touch_term: attribution.first_touch_term || null,
    // Last-touch attribution
    last_touch_source: attribution.last_touch_source || null,
    last_touch_medium: attribution.last_touch_medium || null,
    last_touch_campaign: attribution.last_touch_campaign || null,
    last_touch_content: attribution.last_touch_content || null,
    last_touch_term: attribution.last_touch_term || null,
    // Click IDs
    gclid: attribution.gclid || null,
    fbclid: fbclid,
    ttclid: attribution.ttclid || null,
    ttp: attribution.ttp || null,
    msclkid: attribution.msclkid || null,
    // UTMs estendidos (GA4 campaign params)
    utm_id: attribution.utm_id || null,
    utm_source_platform: attribution.utm_source_platform || null,
    // Facebook cookies (para sGTM → Meta CAPI)
    'x-fb-ck-fbc': fbc,
    'x-fb-ck-fbp': fbp,
    // Metadata (não vai para o dataLayer, só para debug)
    _attribution_source: dataSource,
  };
};

export const setTrackingUserData = (input = {}) => {
  if (typeof window === 'undefined') return;

  const email_address = normalizeEmail(input.email || input.email_address);
  const phone_number_meta = normalizePhoneToDigits(input.phone || input.phone_number || input.telefone);
  const phone_number = phone_number_meta; // Mantemos igual (digits com DDI) para GA4 user_data

  const fullName = input.full_name || input.nome || input.name;
  const nameParts = splitFullName(fullName);
  const first_name = input.first_name || input.firstname || input.fn || nameParts.first_name;
  const last_name = input.last_name || input.lastname || input.ln || nameParts.last_name;

  // Endereço fiscal (billing_preferences.billing_address)
  const billing_address = input.billing_address || input.billingAddress;

  const address = {
    first_name,
    last_name,
    city: normalizeCity(input.city || input.cidade || billing_address?.city),
    region: normalizeState(input.region || input.state || input.estado || billing_address?.state),
    postal_code: normalizePostalCode(input.postal_code || input.zipcode || input.cep || billing_address?.zipcode),
    country: normalizeCountry(input.country || input.pais),
  };

  const user_data = {
    email_address,
    phone_number,
    phone_number_meta,
    address,
    external_id: input.external_id || input.user_id,
  };

  // Formato “Meta Ads” (útil para mapear direto em tags).
  const fb_user_data = {
    em: email_address,
    ph: phone_number_meta,
    fn: first_name,
    ln: last_name,
    ct: address.city,
    st: address.region,
    zp: address.postal_code,
    country: address.country,
    external_id: user_data.external_id,
  };

  // Atribuição direta por spread (não deepMerge) para preservar valores em user_data
  // mesmo quando outros campos são undefined — deepMerge descarta chaves com valor
  // undefined, o que fazia o objeto user_data ficar vazio quando chamado com dados
  // parciais (ex: sem billing_address, sem city, etc.).
  const prev = window[TRACKING_USER_DATA_KEY] || {};
  window[TRACKING_USER_DATA_KEY] = {
    ...prev,
    user_data: {
      ...(prev.user_data || {}),
      ...user_data,
      address: { ...(prev.user_data?.address || {}), ...address },
    },
    fb_user_data: { ...(prev.fb_user_data || {}), ...fb_user_data },
    billing_address: billing_address
      ? {
          city: normalizeCity(billing_address.city),
          state: normalizeState(billing_address.state),
          number: billing_address.number ? String(billing_address.number).trim() : undefined,
          street: billing_address.street ? String(billing_address.street).trim() : undefined,
          zipcode: normalizePostalCode(billing_address.zipcode),
          complement: billing_address.complement ? String(billing_address.complement).trim() : undefined,
          neighborhood: billing_address.neighborhood ? String(billing_address.neighborhood).trim() : undefined,
        }
      : prev.billing_address,
  };

  if (!email_address && !phone_number_meta && !first_name) {
    console.warn('⚠️ setTrackingUserData: nenhum dado relevante recebido! Input:', input);
  } else {
    console.log('📊 setTrackingUserData:', {
      email: email_address ? '✅' : '❌',
      phone: phone_number_meta ? '✅' : '❌',
      name: `${first_name || '?'} ${last_name || '?'}`,
      external_id: user_data.external_id ? '✅' : '❌',
    });
  }
};

const enrichWithUserData = (parameters = {}) => {
  if (typeof window === 'undefined') return parameters;
  const stored = window[TRACKING_USER_DATA_KEY] || {};

  // Permite sobrescrever por evento (parameters.user_data etc.)
  const merged = deepMerge(stored, parameters);

  // Garantir que o objeto user_data.address exista (para variáveis do sGTM).
  if (merged.user_data && !merged.user_data.address) {
    merged.user_data.address = {};
  }

  return merged;
};

// Gera event_id único para deduplicação (GA4, Meta CAPI, sGTM).
const generateEventId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

/**
 * Nomes de conversão que o GTM web publicado (GTM-PGMKV4RW) escuta.
 * Trocar um nome aqui sem trocar o trigger no GTM antes zera a conversão em GA4, Meta e Google Ads:
 * foi o que aconteceu com o trial de 2026-08-18 a 2026-10-11 (app mandava start_trial, GTM só ouvia InitiateTrial).
 * scripts/check-gtm-contract.py confere esta lista contra o container publicado no pré-deploy.
 */
export const CONVERSION_EVENTS = {
  completeRegistration: 'CompleteRegistration',
  firstAdAccountLinked: 'FirstAdAccountLinked',
  beginCheckout: 'begin_checkout',
  startTrial: 'InitiateTrial',
  purchase: 'purchase',
};

// Envia eventos para o dataLayer (GTM).
const trackEvent = (eventName, parameters = {}) => {
  const enrichedParams = enrichWithUserData(parameters);
  const eventId = parameters.event_id ?? enrichedParams.event_id ?? generateEventId();

  const payload = {
    ...enrichedParams,
    event: eventName,
    event_id: eventId,
  };

  // Diagnóstico para eventos de conversão (os que o sGTM encaminha para Meta/Google).
  const isConversion = Object.values(CONVERSION_EVENTS).includes(eventName);
  if (isConversion) {
    const ud = payload.user_data || {};
    console.log(`📊 dataLayer.push [${eventName}]:`, {
      email: ud.email_address ? '✅' : '❌',
      phone: ud.phone_number_meta ? '✅' : '❌',
      first_name: ud.address?.first_name ? '✅' : '❌',
      last_name: ud.address?.last_name ? '✅' : '❌',
      external_id: ud.external_id ? '✅' : '❌',
      fbc: payload['x-fb-ck-fbc'] ? '✅' : '❌',
      fbp: payload['x-fb-ck-fbp'] ? '✅' : '❌',
      gclid: payload.gclid ? '✅' : '❌',
      event_id: eventId,
    });
    if (!ud.email_address) {
      console.warn(`⚠️ [${eventName}] user_data.email_address está VAZIO! window store:`,
        JSON.stringify(window[TRACKING_USER_DATA_KEY]?.user_data || {}, null, 2));
    }
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

// ===== EVENTOS RECOMENDADOS PELO GOOGLE ANALYTICS 4 =====

/**
 * Evento recomendado: login
 * Rastreia quando um usuário faz login
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', {
    method: method, // Método de login usado (google, email, facebook, etc.)
  });
};

/**
 * Evento recomendado: sign_up
 * Rastreia quando um usuário se registra
 */
export const trackSignUp = (method = 'email') => {
  trackEvent('sign_up', {
    method: method, // Método de registro usado
  });
};

/**
 * Evento recomendado: search
 * Rastreia pesquisas realizadas no site
 */
export const trackSearch = (searchTerm) => {
  trackEvent('search', {
    search_term: searchTerm, // Termo de busca
  });
};

/**
 * Evento recomendado: view_item
 * Rastreia visualização de itens/produtos
 */
export const trackViewItem = (itemId, itemName, category, value, currency = 'BRL') => {
  trackEvent('view_item', {
    currency: currency,
    value: value,
    items: [{
      item_id: itemId,
      item_name: itemName,
      category: category,
      price: value,
      quantity: 1,
    }],
  });
};

/**
 * Evento recomendado: add_to_cart
 * Rastreia adição de itens ao carrinho
 */
export const trackAddToCart = (itemId, itemName, category, value, currency = 'BRL') => {
  trackEvent('add_to_cart', {
    currency: currency,
    value: value,
    items: [{
      item_id: itemId,
      item_name: itemName,
      category: category,
      price: value,
      quantity: 1,
    }],
  });
};

/**
 * Evento recomendado: begin_checkout
 * Rastreia início do processo de checkout
 */
export const trackBeginCheckout = (value, currency = 'BRL', items = []) => {
  trackEvent(CONVERSION_EVENTS.beginCheckout, {
    currency: currency,
    value: value,
    items: items,
  });
};

/**
 * Evento recomendado: purchase
 * Rastreia compras concluídas
 * Busca dados de atribuição do banco antes de disparar
 */
export const trackPurchase = async (transactionId, value, currency = 'BRL', items = [], parameters = {}) => {
  try {
    // Buscar dados de atribuição do banco
    const enrichedParams = await enrichWithAttributionFromDB(parameters);
    
    // Remover metadata antes de enviar para dataLayer
    const { _attribution_source, ...dataLayerParams } = enrichedParams;

    const resolvedItems = (Array.isArray(items) && items.length > 0)
      ? items
      : [{
          item_id: parameters.plan_slug || parameters.plan || parameters.package_id || 'unknown',
          item_name: parameters.content_name || parameters.plan_name || `Plano ${parameters.plan || ''}`.trim(),
          item_category: parameters.content_category || 'Subscription',
          price: Number(value) || 0,
          quantity: 1,
        }];
    
    const stableEventId = parameters.event_id || (transactionId ? String(transactionId) : undefined);
    trackEvent(CONVERSION_EVENTS.purchase, {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: resolvedItems,
      content_name: parameters.content_name || resolvedItems[0]?.item_name,
      content_category: parameters.content_category || resolvedItems[0]?.item_category,
      ...dataLayerParams,
      ...(stableEventId ? { event_id: stableEventId } : {}),
    });
    
    console.log(`📊 purchase (GA4) disparado (fonte: ${_attribution_source}):`, {
      transactionId,
      value,
      items: resolvedItems.length,
      first_touch_source: dataLayerParams.first_touch_source,
      utm_id: dataLayerParams.utm_id ? '✅' : '❌',
    });
  } catch (e) {
    // Fallback: disparar sem dados do banco
    console.warn('📊 purchase - Erro ao buscar atribuição, disparando sem:', e);
    const stableEventId = parameters.event_id || (transactionId ? String(transactionId) : undefined);
    trackEvent(CONVERSION_EVENTS.purchase, {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: items,
      ...parameters,
      ...(stableEventId ? { event_id: stableEventId } : {}),
    });
  }
};

/**
 * Evento recomendado: generate_lead
 * Rastreia geração de leads (formulários preenchidos)
 */
export const trackGenerateLead = (value, currency = 'BRL') => {
  trackEvent('generate_lead', {
    currency: currency,
    value: value,
  });
};

/**
 * Evento personalizado: CompleteRegistration
 * Busca dados de atribuição do banco antes de disparar.
 * Aceita user_id em parameters para setar antes da busca de atribuição.
 */
export const trackCompleteRegistration = async (method = 'email', parameters = {}) => {
  try {
    // Setar userId antes de buscar atribuição (o usuário acabou de ser criado
    // e ainda não fez login; sem isso getCurrentUserId() retorna null e
    // fetchAttributionFromDB não consegue buscar os dados do Supabase).
    if (parameters.user_id) {
      setCurrentUserId(parameters.user_id);
    }

    const enrichedParams = await enrichWithAttributionFromDB(parameters);
    const { _attribution_source, ...dataLayerParams } = enrichedParams;
    
    trackEvent(CONVERSION_EVENTS.completeRegistration, {
      registration_method: method,
      ...dataLayerParams,
    });
    
    console.log(`📊 CompleteRegistration disparado (fonte: ${_attribution_source}):`, {
      method,
      first_touch_source: dataLayerParams.first_touch_source,
      gclid: dataLayerParams.gclid ? '✅' : '❌',
      fbclid: dataLayerParams.fbclid ? '✅' : '❌',
      ttclid: dataLayerParams.ttclid ? '✅' : '❌',
      ttp: dataLayerParams.ttp ? '✅' : '❌',
      fbc: dataLayerParams['x-fb-ck-fbc'] ? '✅' : '❌',
      fbp: dataLayerParams['x-fb-ck-fbp'] ? '✅' : '❌',
    });
  } catch (e) {
    console.warn('📊 CompleteRegistration - Erro ao buscar atribuição, disparando sem:', e);
    trackEvent(CONVERSION_EVENTS.completeRegistration, {
      registration_method: method,
      ...parameters,
    });
  }
};

/**
 * Evento personalizado: LinkAccount (toda conta conectada).
 * Na primeira conta de anúncios do usuário também sai FirstAdAccountLinked, decidido pelo backend
 * (uma vez por usuário; Search Console e Instagram orgânico não contam).
 */
export const trackLinkAccount = async (provider, accountId, parameters = {}) => {
  trackEvent('LinkAccount', {
    provider: provider,
    account_id: accountId,
    ...parameters,
  });

  try {
    const { data } = await api.post('/api/tracking/first-ad-account/', { platform: provider });
    if (!data?.is_first) return;
    const { _attribution_source, ...attribution } = await enrichWithAttributionFromDB({
      provider,
      platform: data.platform || provider,
      account_id: accountId,
      content_name: 'First Ad Account',
      content_category: 'Account Linking',
    });
    trackEvent(CONVERSION_EVENTS.firstAdAccountLinked, attribution);
  } catch (e) {
    console.warn(`📊 ${CONVERSION_EVENTS.firstAdAccountLinked} - não disparado:`, e);
  }
};

/**
 * Trial iniciado. No dataLayer vai como InitiateTrial (CONVERSION_EVENTS.startTrial), o nome do trigger publicado.
 * O sGTM grava no GA4 como start_trial e manda StartTrial à Meta.
 * Espelha o molde ecommerce de purchase: items[], transaction_id, value, currency.
 * Busca dados de atribuição do banco antes de disparar.
 */
export const trackInitiateTrial = async (planName, planPrice, currency = 'BRL', parameters = {}) => {
  const planSlug = parameters.plan_slug || parameters.plan || planName || 'unknown';
  const itemName = parameters.content_name || planName || `Plano ${planSlug}`;
  const transactionId = parameters.transaction_id || parameters.session_id || null;
  const resolvedItems = (Array.isArray(parameters.items) && parameters.items.length > 0)
    ? parameters.items
    : [{
        item_id: planSlug,
        item_name: itemName,
        item_category: parameters.content_category || 'Subscription',
        price: Number(planPrice) || 0,
        quantity: 1,
      }];

  const stableEventId = parameters.event_id || (transactionId ? String(transactionId) : undefined);
  const buildPayload = (extra = {}) => {
    const { event_id: incomingEventId, ...rest } = extra || {};
    const eventId = incomingEventId || stableEventId;
    return {
      plan_name: planName,
      plan_value: planPrice,
      plan_slug: planSlug,
      content_name: itemName,
      content_category: parameters.content_category || 'Subscription',
      content_type: 'product',
      value: planPrice,
      currency: currency,
      items: resolvedItems,
      ...(transactionId ? { transaction_id: transactionId } : {}),
      ...rest,
      ...(eventId ? { event_id: eventId } : {}),
    };
  };

  try {
    if (parameters.user_id) setCurrentUserId(parameters.user_id);
    const enrichedParams = await enrichWithAttributionFromDB(parameters);

    // Remover metadata antes de enviar para dataLayer
    const { _attribution_source, items: _ignoredItems, ...dataLayerParams } = enrichedParams;

    trackEvent(CONVERSION_EVENTS.startTrial, buildPayload(dataLayerParams));

    console.log(`📊 ${CONVERSION_EVENTS.startTrial} disparado (fonte: ${_attribution_source}):`, {
      planName,
      planPrice,
      transactionId,
      items: resolvedItems.length,
      first_touch_source: dataLayerParams.first_touch_source,
      first_touch_campaign: dataLayerParams.first_touch_campaign,
      gclid: dataLayerParams.gclid ? '✅' : '❌',
      fbclid: dataLayerParams.fbclid ? '✅' : '❌',
      fbc: dataLayerParams['x-fb-ck-fbc'] ? '✅' : '❌',
      fbp: dataLayerParams['x-fb-ck-fbp'] ? '✅' : '❌',
    });
  } catch (e) {
    console.warn(`📊 ${CONVERSION_EVENTS.startTrial} - Erro ao buscar atribuição, disparando sem:`, e);
    const { items: _ignoredItems, ...rest } = parameters;
    trackEvent(CONVERSION_EVENTS.startTrial, buildPayload(rest));
  }
};

/**
 * Evento personalizado: Purchase (Stripe)
 * Busca dados de atribuição do banco antes de disparar
 */
export const trackPurchaseCompleted = async (transactionId, value, currency = 'BRL', parameters = {}) => {
  // Mesmo evento GA4 `purchase`. O nome `Purchase` batia no mesmo trigger de anúncio e duplicava.
  await trackPurchase(transactionId, value, currency, parameters.items || [], parameters);
};

// ===== EVENTOS PERSONALIZADOS ESPECÍFICOS PARA GIO =====

/**
 * Rastreia criação de campanhas (evento personalizado)
 */
export const trackCampaignCreated = (campaignType, campaignId) => {
  trackEvent('campaign_created', {
    campaign_type: campaignType,
    campaign_id: campaignId,
    event_category: 'campaign',
    event_label: campaignType,
  });
};

/**
 * Rastreia visualização de campanhas usando view_item
 */
export const trackCampaignViewed = (campaignId, campaignName, campaignType) => {
  trackViewItem(
    campaignId,
    campaignName,
    `Campaign_${campaignType}`,
    0, // Valor 0 para visualizações gratuitas
    'BRL'
  );
};

/**
 * Rastreia edição de campanhas
 */
export const trackCampaignEdited = (campaignId, campaignType) => {
  trackEvent('campaign_edited', {
    campaign_id: campaignId,
    campaign_type: campaignType,
    event_category: 'campaign',
    event_label: `${campaignType}_${campaignId}`,
  });
};

/**
 * Rastreia exclusão de campanhas
 */
export const trackCampaignDeleted = (campaignId, campaignType) => {
  trackEvent('campaign_deleted', {
    campaign_id: campaignId,
    campaign_type: campaignType,
    event_category: 'campaign',
    event_label: `${campaignType}_${campaignId}`,
  });
};

/**
 * Rastreia criação de projetos
 */
export const trackProjectCreated = (projectType, projectId) => {
  trackEvent('project_created', {
    project_type: projectType,
    project_id: projectId,
    event_category: 'project',
    event_label: projectType,
  });
};

/**
 * Rastreia visualização de projetos usando view_item
 */
export const trackProjectViewed = (projectId, projectName, projectType) => {
  trackViewItem(
    projectId,
    projectName,
    `Project_${projectType}`,
    0,
    'BRL'
  );
};

/**
 * Rastreia seleção de planos usando generate_lead
 */
export const trackPlanSelected = (planName, planPrice, currency = 'BRL') => {
  // Primeiro gera lead
  trackGenerateLead(planPrice, currency);

  // Depois registra evento específico
  trackEvent('plan_selected', {
    plan_name: planName,
    plan_value: planPrice,
    currency: currency,
    event_category: 'subscription',
    event_label: planName,
  });
};

/**
 * Rastreia conexão com Google Ads
 */
export const trackGoogleAdsConnected = (accountId) => {
  trackEvent('google_ads_connected', {
    account_id: accountId,
    event_category: 'integration',
    event_label: 'google_ads',
  });
};

/**
 * Rastreia desconexão do Google Ads
 */
export const trackGoogleAdsDisconnected = (accountId) => {
  trackEvent('google_ads_disconnected', {
    account_id: accountId,
    event_category: 'integration',
    event_label: 'google_ads',
  });
};

/**
 * Rastreia erros usando evento personalizado
 */
export const trackError = (errorType, errorMessage) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    event_category: 'error',
  });
};

/**
 * Rastreia cliques em botões
 */
export const trackButtonClick = (buttonName, location) => {
  trackEvent('button_click', {
    button_name: buttonName,
    button_location: location,
    event_category: 'ui_interaction',
  });
};

/**
 * Rastreia downloads de arquivos
 */
export const trackFileDownload = (fileName, fileType) => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    event_category: 'download',
  });
};

/**
 * Rastreia tempo gasto na página
 */
export const trackTimeOnPage = (pageName, timeSpent) => {
  trackEvent('time_on_page', {
    page_name: pageName,
    time_spent: timeSpent,
    event_category: 'engagement',
  });
};

/**
 * Rastreia visualização de página (usando evento recomendado)
 */
export const trackPageView = (pageTitle, pageLocation, pagePath) => {
  trackEvent('page_view', {
    page_title: pageTitle,
    page_location: pageLocation,
    page_path: pagePath,
  });
};

// ===== FUNÇÕES UTILITÁRIAS =====

/**
 * Função genérica para eventos customizados
 */
export const trackCustomEvent = (eventName, parameters = {}) => {
  trackEvent(eventName, parameters);
};

/**
 * Rastreia conversões específicas
 */
export const trackConversion = (conversionType, value, currency = 'BRL') => {
  trackEvent('conversion', {
    send_to: config.googleAnalytics.measurementId,
    event_category: 'conversion',
    event_label: conversionType,
    value: value,
    currency: currency,
  });
};

/**
 * Define custom dimensions (se configuradas no GA4)
 */
export const setCustomDimension = (dimensionName, dimensionValue) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'set_custom_dimension',
    custom_map: {
      [dimensionName]: dimensionValue
    }
  });
};

/**
 * Define user properties
 */
export const setUserProperties = (properties) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'set_user_properties',
    user_properties: properties
  });
};

// ===== FUNÇÕES PARA REGISTRAR CONVERSÕES NO BANCO =====

/**
 * Registra uma conversão no banco de dados
 * Deve ser chamado após o evento ser disparado para manter os dados sincronizados
 */
export const registerConversionInDB = async (conversionType, conversionData = {}) => {
  const userId = getCurrentUserId();
  
  if (!userId) {
    console.warn(`📊 registerConversionInDB - User ID não encontrado para ${conversionType}`);
    return null;
  }
  
  try {
    const response = await api.patch('/api/user-utm-tracking/update/', {
      user_id: userId,
      conversion_type: conversionType,
      conversion_data: {
        timestamp: new Date().toISOString(),
        ...conversionData,
      },
    });
    
    console.log(`📊 Conversão '${conversionType}' registrada no banco:`, response.data);
    return response.data;
  } catch (error) {
    console.warn(`📊 Erro ao registrar conversão '${conversionType}' no banco:`, error.message);
    return null;
  }
};

/**
 * Evento InitiateTrial completo (dispara evento + registra no banco)
 */
export const trackAndRegisterInitiateTrial = async (planName, planPrice, currency = 'BRL', parameters = {}) => {
  // Disparar evento com dados de atribuição
  await trackInitiateTrial(planName, planPrice, currency, parameters);
  
  // Registrar conversão no banco
  await registerConversionInDB('initiate_trial', {
    plan_name: planName,
    plan_value: planPrice,
    currency: currency,
    trial_days: parameters.trial_days || 14,
  });
};

/**
 * Evento Purchase completo (dispara evento + registra no banco)
 */
export const trackAndRegisterPurchase = async (transactionId, value, currency = 'BRL', parameters = {}) => {
  // Disparar evento com dados de atribuição
  await trackPurchaseCompleted(transactionId, value, currency, parameters);
  
  // Registrar conversão no banco
  await registerConversionInDB('purchase', {
    transaction_id: transactionId,
    value: value,
    currency: currency,
    plan: parameters.plan || null,
    payment_method: parameters.payment_method || 'stripe',
  });
};

// ===== ALIASES PARA COMPATIBILIDADE =====
export const trackUserLogin = trackLogin;
export const trackUserRegistration = trackSignUp;
export const trackSearchPerformed = trackSearch;
export const trackDownloadStarted = trackFileDownload;
